/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import {
  Checkbox,
  Dropdown,
  DropdownMenuItemType,
  ICheckboxStyles,
  IDropdownOption,
  IDropdownStyles,
  IStackStyles,
  IStackTokens,
  Spinner,
  SpinnerSize,
  Stack,
  TextField,
  TooltipHost,
} from "@fluentui/react";
import * as Messages from "../../codegen/Messages";
import { buildTestId, ComponentTestIds } from "../../helpers/testIdHelper";
import { BaseInputProps } from "./BaseInput";
import {
  FormContext,
  FormErrorsContext,
  FormGroupValidationModeContext,
  FormInputGroupLayoutContext,
  FormState,
  FormValidationMode,
  FormValidationModeContext,
  getFieldErrors,
  ValidationState,
} from "./Form";
import { buildLabelWrapperTestIds, LabelWrapper, LabelWrapperTestIds } from "./LabelWrapper";

export const OptionNewId = "__new__";
export interface SelectComponent {
  /**
   * Function to see text and data for the new option
   */
  setOptionNew: (text: string, data: any) => void;
  /**
   * Function to get the new option
   */
  getOptionNew: () => string | undefined;
}

export interface GroupOption {
  /**
   * If passed, it will show the options under this heading.
   */
  heading: string;
  /**
   * Array of options, each option is individual select row.
   */
  options: SelectOption[];
  /**
   * data-test-id HTML attribute value
   */
  testId?: string;
}

export interface SelectOption {
  /**
   * Whether or not the option is selected
   * @default false
   */
  selected?: boolean;
  /**
   * Unique identifier for the select option
   */
  id: string;
  /**
   * Text to render for this option
   */
  text: string;
  /**
   * Data to render for this option
   */
  data?: any;
  /**
   * Makes option not selectable
   * @default false
   */
  disabled?: boolean;
  /**
   * Explanation of option
   */
  tooltip?: string;
  /**
   * Description for the option
   */
  description?: string;
  /**
   * data-test-id HTML attribute value
   */
  testId?: string;
}

export interface SelectProps extends BaseInputProps {
  /**
   * Function to get the component reference for te option
   */
  componentRef?: (component: SelectComponent) => void;
  /**
   * Label for the select field
   */
  label?: string;
  /**
   *Options to be listed for selection
   */
  options: SelectOption[] | GroupOption[];
  /**
   *Value(s) to be selected initially
   */
  defaultValue?: string[];
  /**
   * Option to select multiple values
   */
  multiSelect?: boolean;
  /** Select all the options when the multiSelect mode is turned on */
  selectAllOption?: boolean;
  /**
   * Hide search box
   * @default false
  */
  hideSearchBox?: boolean;
  /**
   * Show the number of options selected
   */
  showCount?: boolean;
  /**
   * Display the spinner when options are loading
   */
  loading?: boolean;
  /**
   * Callback to validate the selected option
   */
  validator?: (value: SelectOption[]) => string[] | undefined;
  /**
   * Callback when an option is selected or existing selection is changed
   */
  onChange?: (id: string) => void;
  /**
   * Callback invoked when the dropdown is clicked on and before it opens
   */
  onClick?: () => void;
  /**
   * ClassName for the select field
   * NOTE: Only takes effect when the FormInputGroupLayout is COMPACT
   */
  className?: string | undefined;
}

const dropdownStyles: Partial<IDropdownStyles> = {
  root: {
    width: "100%",
    marginTop: "5px !important",
  },
  dropdownOptionText: { overflow: "visible", whiteSpace: "normal" },
  title: { height: "24px", lineHeight: "20px" },
  caretDownWrapper: { lineHeight: "25px" },
  dropdownItem: {
    height: "auto",
    "&:hover": { color: "#000000" },
  },
  dropdownItemSelected: { height: "auto" },
  dropdownItems: {
    "div[aria-labelledby$='searchItems']": { overflow: "auto", maxHeight: 300 },
    "div[id$='o4aSearchBox']": { display: "block !important" },
    "#searchItems": { display: "none" },
  },
  dropdownItemHeader: {
    color: "#000000",
    "&:hover": { color: "#000000" },
  },
};

const dropdownLoadingStyles: Partial<IDropdownStyles> = {
  root: {
    width: "100%",
    minWidth: "100px",
    marginTop: "5px !important",
  },
  title: { height: "24px", lineHeight: "20px", border: "1px solid #605e5c", borderRadius: "2px" },
};

const checkBoxStyles: Partial<ICheckboxStyles> = { root: { paddingTop: "10px" } };

interface IDropdownOptionExt extends IDropdownOption {
  testId?: string;
  tooltip?: string;
}

export interface SelectTestIds extends ComponentTestIds, LabelWrapperTestIds {
  searchBox: string;
  selectAllOption: string;
}

export const buildSelectTestIds = (testId?: string): SelectTestIds => ({
  ...buildLabelWrapperTestIds(testId),
  searchBox: buildTestId(testId, "-search-box"),
  selectAllOption: buildTestId(testId, "-select-all-option"),
  component: buildTestId(testId),
});

/**
 * The Select component renders  a single select or a multi select options where the user can either select a
 * single or multiple options from the list.
 */
export const Select = ({
  testId,
  fieldName,
  groupName,
  componentRef,
  label,
  options,
  disabled,
  required,
  placeholder,
  defaultValue,
  multiSelect,
  tooltip,
  inputLink,
  subField,
  statusInfo,
  selectAllOption,
  hideSearchBox = false,
  showCount,
  validator,
  onChange,
  onClick,
  loading,
  className,
}: SelectProps): JSX.Element => {
  const { groupName: layoutGroup, layout } = React.useContext(
    FormInputGroupLayoutContext,
  );
  const group = groupName || layoutGroup;
  const form: FormState = React.useContext(FormContext);
  if (!Object.keys(form).length) {
    throw new Error("Select should be used within form");
  }
  const validationMode = React.useContext(FormValidationModeContext);
  const groupValidationMode = React.useContext(FormGroupValidationModeContext);
  const formErrors = React.useContext(FormErrorsContext);
  const fieldErrors = getFieldErrors(formErrors, fieldName, group);
  const [errorMessage, setErrorMessage] = React.useState<string[]>();
  const [selectedKey, setSelectedKeys] = React.useState<string[]>();
  const [searchText, setSearchText] = React.useState<string>();

  const newOptionRef = React.useRef<SelectOption>();

  const allErrors = [...errorMessage || [], ...fieldErrors || []];

  const selectTestIds = buildSelectTestIds(testId);

  const selectOptions: IDropdownOptionExt[] = [];
  options.forEach((option, i: number) => {
    if ((option as GroupOption).heading) {
      const groupOption = option as GroupOption;
      selectOptions.push({
        key: `${i}-header`,
        text: groupOption.heading,
        itemType: DropdownMenuItemType.Header,
        testId: groupOption.testId,
        index: i,
      });
      groupOption.options.forEach(groupedItem => {
        selectOptions.push({
          key: groupedItem.id,
          text: groupedItem.text,
          data: groupedItem.data,
          disabled: groupedItem.disabled,
          tooltip: groupedItem.tooltip,
          ariaLabel: groupedItem.description,
          testId: groupedItem.testId,
          index: i,
        });
      });
      selectOptions.push({
        key: `${i}-divider`,
        text: "-",
        itemType: DropdownMenuItemType.Divider,
        index: i,
      });
    } else {
      const o = option as SelectOption;
      selectOptions.push({
        key: o.id,
        text: o.text,
        data: o.data,
        disabled: o.disabled,
        tooltip: o.tooltip,
        ariaLabel: o.description,
        testId: o.testId,
        index: i,
      });
    }
  });

  const isDescriptiveText = selectOptions.find(option => option?.ariaLabel !== undefined);
  if (newOptionRef.current) {
    selectOptions.unshift({ key: newOptionRef.current?.id, text: `(New) ${newOptionRef.current?.text}` });
  }

  if (selectAllOption && selectOptions.length) {
    selectOptions.unshift(
      {
        key: "selectAll",
        text: " ",
        // Keep as Header to allow for overriding the checkbox onChange event handler
        itemType: DropdownMenuItemType.Header,
      },
    );
  }

  if (!hideSearchBox && selectOptions.length) {
    selectOptions.unshift(
      {
        key: "o4aSearchBox",
        text: "",
        itemType: DropdownMenuItemType.Header,
      },
      {
        id: "searchItems",
        key: "searchItems",
        text: "",
        itemType: DropdownMenuItemType.Header,
      },
    );
  }

  const fieldValidation = (): void => {
    const errorMsg = validator ? validator(form.getValue(fieldName, group)) : undefined;
    const requiredMessage = required ? form.getValue(fieldName, group)?.[0]?.id === undefined
      ? [Messages.validation.requiredValidation()] : undefined : undefined;
    const errors = [...requiredMessage || [], ...errorMsg || []];
    form.setFieldState(
      errors && errors?.length > 0 ? ValidationState.INVALID : ValidationState.VALID,
      fieldName,
      group,
    );
    setErrorMessage(errors);
  };

  const setOptionNew = (text: string, data: any): void => {
    const optionNew = { id: OptionNewId, text, data };
    newOptionRef.current = optionNew;
    setSelectedKeys([OptionNewId]);
    form.setValue([optionNew], fieldName, group);
    if (validator || required) {
      fieldValidation();
    }
    onChange?.(OptionNewId);
  };

  const getOptionNew = (): string | undefined => newOptionRef?.current?.text;

  let newSelection: SelectOption[] = form?.getValue(fieldName, group) !== undefined
    ? form?.getValue(fieldName, group)
    : [];

  const setSelectedOptions = (): void => {
    if (selectedKey?.length !== selectOptions.filter(opt => !opt.disabled).length - 1) {
      // eslint-disable-next-line max-len
      const selectedOptions = (options as SelectOption[])?.filter(option => option.id !== "selectAll" && !option.disabled);
      setSelectedKeys(selectedOptions.map(opt => opt.id));
      newSelection = selectedOptions;
    } else {
      setSelectedKeys([]);
      newSelection = [];
    }
    form.setValue(newSelection, fieldName, group);
    if (validator || required) {
      fieldValidation();
    }
    onChange?.("selectAll");
  };

  React.useEffect(() => {
    if (componentRef) {
      componentRef({ setOptionNew, getOptionNew } as SelectComponent);
    }

    form.registerFieldLabel(fieldName, label);
    form.registerFieldGroup(fieldName, group);

    if (validator || required) {
      form.registerField(fieldName, group);
    }
    if (defaultValue) {
      let selectedOptions:SelectOption[] = [];
      if ((options as GroupOption[])?.[0]?.heading) {
        (options as GroupOption[])?.forEach(optionGroup => {
          optionGroup.options.forEach(o => {
            if (defaultValue.includes(o.id)) {
              selectedOptions.push(o);
            }
          });
        });
      } else {
        selectedOptions = (options as SelectOption[])?.filter(option => defaultValue.includes(option.id as string));
      }
      setSelectedKeys(defaultValue);
      form.setValue(selectedOptions, fieldName, group);
      fieldValidation();
    }
    return function cleanup() {
      if (validator || required) {
        form.unRegisterField(fieldName, group);
      }
      newOptionRef.current = undefined;
    };
    // Only at mount time. Others props should not have side effect beyond their initial values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const internalOnChange = (
    _: React.FormEvent<HTMLDivElement>,
    dropdownOption?: IDropdownOption,
  ): void => {
    if (multiSelect && dropdownOption !== undefined) {
      if (dropdownOption.selected) {
        newSelection.push(dropdownOption2SelectOption(dropdownOption));
      } else {
        newSelection = newSelection.filter(item => item.id !== dropdownOption.key);
      }
      setSelectedKeys(newSelection.map(key => key.id));
      form.setValue(newSelection, fieldName, group);
    } else {
      setSelectedKeys([dropdownOption?.key as string]);
      newSelection = [];
      newSelection.push(dropdownOption2SelectOption(dropdownOption));
      form.setValue(newSelection, fieldName, group);
    }

    if (validator || required) {
      fieldValidation();
    }
    onChange?.(dropdownOption?.key.toString() || "");
  };

  const dropdownOption2SelectOption = (dropdownOption: IDropdownOption | undefined): SelectOption => {
    if (dropdownOption?.key === OptionNewId && newOptionRef.current) {
      return { id: newOptionRef.current?.id, text: newOptionRef.current?.text, data: newOptionRef.current?.data };
    }
    return { id: dropdownOption?.key as string, text: dropdownOption?.text as string, data: dropdownOption?.data };
  };

  React.useEffect(() => {
    if (validationMode === FormValidationMode.ON || groupValidationMode[group] === FormValidationMode.ON) {
      if (validator || required) {
        fieldValidation();
      }
    }
    // Only if validation mode changes. Others props should not have side effect beyond their initial values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationMode, groupValidationMode]);

  const showCountPlaceHolderText = placeholder === undefined ? Messages.hints.showSelection("0") : placeholder;

  const onRenderTitle = (): JSX.Element => (
    <span>
      {Messages.hints.showSelection(form?.getValue(fieldName, group).length)}
    </span>
  );

  const onRenderCaretDown = (): JSX.Element => (
    <Spinner
      styles={{ root: { marginTop: 4 } }}
      size={SpinnerSize.xSmall}
    />
  );

  const stackTokens: IStackTokens = { childrenGap: 5 };
  const stackStyles: IStackStyles = isDescriptiveText ? { root: { marginTop: "10px" } } : {};

  const onRenderOption = (option?: IDropdownOptionExt): JSX.Element => (
    <TooltipHost content={option?.tooltip}>
      {option?.key === "selectAll" && (
        <Checkbox
          styles={checkBoxStyles}
          indeterminate={selectedKey?.length !== selectOptions.filter(
            opt => !opt.disabled,
          ).length - 1 && (selectedKey?.length ?? 0) !== 0}
          checked={selectedKey?.length === selectOptions.filter(opt => !opt.disabled).length - 1}
          label={Messages.actions.selectAll()}
          onChange={setSelectedOptions}
          inputProps={{ "data-test-id": selectTestIds.selectAllOption } as Record<string, string | undefined>}
        />
      )}
      {option?.itemType === DropdownMenuItemType.Header && option?.key === "o4aSearchBox" && (
        <TextField
          styles={{ root: { marginTop: "5px" }, fieldGroup: { height: "25px" } }}
          placeholder={Messages.hints.selectExisting()}
          onChange={(_, val) => setSearchText(val)}
          data-test-id={selectTestIds.searchBox}
        />
      )}
      <Stack
        tokens={stackTokens}
        styles={stackStyles}
        data-test-id={option?.testId}
      >
        <span style={isDescriptiveText ? { fontWeight: "600" } : {}}>{option?.text}</span>
        {option?.ariaLabel !== undefined && (
          <span style={{ fontWeight: "300" }}>{option?.ariaLabel}</span>
        )}
      </Stack>
    </TooltipHost>
  );

  const displayPlaceholder = loading
    ? Messages.common.loading()
    : showCount
      ? showCountPlaceHolderText
      : placeholder;

  let styles: Partial<IDropdownStyles> = dropdownStyles;
  if (loading) styles = dropdownLoadingStyles;

  const normalizeItems = (items: IDropdownOptionExt[]): IDropdownOptionExt[] => {
    const indexTrace: number[] = [];
    let showSearchBoxWhenOnlyHeaders = true;
    const itemsToShow = items.map(option => {
      if (searchText?.trim() !== ""
        && option.itemType !== DropdownMenuItemType.Header
        && option.text.toLowerCase().indexOf(searchText?.trim().toLowerCase() || "") > -1) {
        if (option?.index !== undefined) {
          if (!indexTrace.includes(option?.index)) {
            indexTrace.push(option?.index);
          }
        }
        return option;
      }
      if (searchText?.trim() !== ""
        && option.key !== "searchItems"
        && option.key !== "o4aSearchBox"
        && option.text.toLowerCase().indexOf(searchText?.trim().toLowerCase() || "") < 0) {
        return { ...option, hidden: true };
      }
      // when searchbox is empty
      return option;
    });

    if (!searchText?.trim()) {
      indexTrace.length = 0;
      showSearchBoxWhenOnlyHeaders = false;
    }

    indexTrace.forEach(i => {
      itemsToShow.forEach(item => {
        if (item.index === i && item.itemType === DropdownMenuItemType.Header) {
          item.hidden = false;
        } else if (item.index === i) {
          showSearchBoxWhenOnlyHeaders = false;
        }
      });
    });
    let finalItems: IDropdownOptionExt[] = [];
    if (showSearchBoxWhenOnlyHeaders) {
      finalItems = itemsToShow.filter(item => item.key === "searchItems" || item.key === "o4aSearchBox");
    } else {
      finalItems = itemsToShow.filter(item => item.hidden !== true);
    }

    return finalItems;
  };

  return (
    <LabelWrapper
      errors={allErrors}
      fieldName={fieldName}
      layout={layout}
      className={className}
      inputLink={inputLink}
      label={label}
      required={required}
      statusInfo={statusInfo}
      subField={subField}
      tooltip={tooltip}
      testId={testId}
    >
      <Dropdown
        id={fieldName}
        data-test-id={selectTestIds.component}
        options={normalizeItems(selectOptions)}
        onChange={internalOnChange}
        onClick={onClick}
        onRenderTitle={showCount ? onRenderTitle : undefined}
        disabled={loading || disabled}
        placeholder={displayPlaceholder}
        multiSelect={multiSelect}
        defaultSelectedKeys={selectedKey}
        defaultSelectedKey={selectedKey}
        onRenderCaretDown={loading ? onRenderCaretDown : undefined}
        onRenderOption={onRenderOption}
        calloutProps={hideSearchBox ? { calloutMaxHeight: 300 } : undefined}
        onDismiss={() => setSearchText("")}
        styles={styles}
      />
    </LabelWrapper>
  );
};
